import { Dialog } from "@headlessui/react";
import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Check,
  Info,
  Play,
  X,
  Download,
} from "lucide-react";
import { Game, RosterEntry } from "@/types";
import CopyButton from "@/components/CopyButton";
import { useAccountStore } from "@/store/account-store";
import { useRosterStore } from "@/store/roster-store";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { useMultipleVideoMetadata } from "@/hooks/useVideoMetadata";
import { Skeleton } from "@/components/ui/Skeleton";

interface Props {
  game: Game | null;
  open: boolean;
  onClose: () => void;
}

export function GameDetailSheet({ game, open, onClose }: Props) {
  const { activeAccountId, accounts } = useAccountStore();
  const rosterStore = useRosterStore();
  const [affiliateSlot, setAffiliateSlot] = useState<RosterEntry | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
  const [videoLoaded, setVideoLoaded] = useState<Record<number, boolean>>({});
  
  // Fetch video metadata
  const { metadataList, loading: metadataLoading } = useMultipleVideoMetadata(
    game?.gameplay || []
  );

  // Helpers: parse duration formats that may be stored in Firestore
  const parseDurationString = (s: string | number | undefined): number => {
    if (!s && s !== 0) return 0;
    if (typeof s === 'number') return Math.floor(s);
    const parts = String(s).split(':').map((p) => parseInt(p, 10));
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + (parts[2] || 0);
    if (parts.length === 2) return parts[0] * 60 + (parts[1] || 0);
    return Number(s) || 0;
  };

  const formatDurationDisplay = (v: number | string | undefined): string => {
    if (typeof v === 'string') return v;
    if (typeof v === 'number' && !Number.isNaN(v)) {
      const total = Math.floor(v);
      const mm = Math.floor(total / 60);
      const ss = total % 60;
      return `${mm}:${String(ss).padStart(2, '0')}`;
    }
    return metadataLoading ? 'Loading...' : 'Unknown';
  };

  useEffect(() => {
    if (!open) {
      setAffiliateSlot(null);
      setError(null);
      setVideoLoaded({});
    }
  }, [open]);

  const activeAccount = useMemo(
    () => accounts.find((acc) => acc.id === activeAccountId) ?? null,
    [accounts, activeAccountId]
  );

  if (!game) return null;

  const confirmed = Boolean(affiliateSlot);

  const handlePromote = async () => {
    setError(null);
    setLoading(true);
    try {
      if (!activeAccountId) {
        throw new Error("Select a social account to use one of its 3 slots.");
      }
      const entry = await rosterStore.addEntry(game, activeAccountId, `${game.slug}-global`);
      setAffiliateSlot(entry);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to promote right now.");
    } finally {
      setLoading(false);
    }
  };

  // Extract video metadata - used for displaying gameplay videos
  game.gameplay.map((url) => {
    // Extract duration from URL (e.g., "15s", "30sec", "60seconds", "1m")
    const durationMatch = url.match(/(\d+)(s|sec|seconds|m|min|minutes)/i);
    let durationSeconds = 0;
    if (durationMatch) {
      const num = parseInt(durationMatch[1], 10);
      const unit = durationMatch[2].toLowerCase();
      if (unit.startsWith('m')) {
        durationSeconds = num * 60;
      } else {
        durationSeconds = num;
      }
    }
    
    // Estimate size based on duration (rough estimate: ~1MB per 5 seconds for 1080p)
    const estimatedSizeMB = durationSeconds > 0 ? Math.round((durationSeconds / 5) * 10) / 10 : null;
    
    return {
      url,
      duration: durationSeconds > 0 ? `${durationSeconds}s` : 'Unknown',
      durationSeconds,
      size: estimatedSizeMB ? `~${estimatedSizeMB}MB` : null,
    };
  });

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/90" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="relative mx-auto w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-3xl border border-white/10 bg-zinc-950 p-6 text-white">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 z-10 rounded-full bg-white/10 p-2 transition hover:bg-white/20"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="flex gap-6">
            {/* Left column: Content */}
            <div className="flex-1">
              <div className="mb-4 flex items-center gap-3">
                <img
                  src={game.appIcon}
                  alt={game.name}
                  width={64}
                  height={64}
                  className="rounded-2xl border border-white/10"
                />
                <div>
                  <Dialog.Title className="text-lg font-semibold">
                    {game.name}
                  </Dialog.Title>
                  <p className="text-sm text-white/60">
                    Ends {format(new Date(game.endDate), "MMM d, yyyy")}
                  </p>
                </div>
              </div>

              <p className="mb-3 text-sm text-white/70">{game.description}</p>
              {game.requirements && (
                <div className="mb-4 rounded-2xl border border-white/10 bg-white/5 p-3 text-sm text-white/80">
                  <p className="flex items-center gap-2 font-medium">
                    <Info className="h-4 w-4" /> Creative Notes
                  </p>
                  <p className="text-white/70">{game.requirements}</p>
                </div>
              )}

              <div className="mb-3 rounded-2xl border border-white/10 bg-white/5 p-3 text-sm text-white/80">
                <p className="font-medium text-white">This game will fill one of your slots.</p>
                <p className="text-white/60">
                  You can run 3 campaigns per social account. Slots reset once you remove a game.
                </p>
              </div>

              {error && (
                <p className="mb-3 rounded-2xl border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
                  {error}
                </p>
              )}

              {!confirmed && (
                <button
                  onClick={handlePromote}
                  disabled={loading}
                  className="mb-3 flex w-full items-center justify-center gap-2 rounded-2xl bg-lime-400 px-4 py-3 text-center font-semibold text-zinc-950 transition hover:bg-lime-300 disabled:opacity-50"
                >
                  <Play className="h-4 w-4" /> {loading ? "Processing..." : "Add to Roaster"}
                </button>
              )}

              {confirmed && affiliateSlot && (
                <div className="mb-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4">
                  <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-emerald-200">
                    <Check className="h-4 w-4" /> Slot confirmed
                  </p>
                  <p className="text-xs text-white/70">Affiliate link</p>
                  <div className="mt-1 flex items-center gap-2 rounded-xl border border-white/10 bg-black/40 p-2 text-xs">
                    <span className="grow break-all">{affiliateSlot.affiliateLink}</span>
                    <CopyButton text={affiliateSlot.affiliateLink} />
                  </div>
                </div>
              )}

              {!confirmed && (
                <div className="mt-4 flex items-center justify-end text-xs">
                  <div className="flex items-center gap-1 text-amber-200">
                    <AlertTriangle className="h-3 w-3" />
                    Slots remaining: {activeAccount ? activeAccount.slotsLimit - activeAccount.slotsUsed : 0}
                  </div>
                </div>
              )}
            </div>

            {/* Right column: Large video */}
            <div className="flex-shrink-0" style={{ width: '280px' }}>
              {game.gameplay && game.gameplay.length > 0 ? (
                <div className="space-y-3">
                  {/* Main video player */}
                  <div className="relative group">
                    <div className="relative" style={{ aspectRatio: '9/16' }}>
                      {!videoLoaded[selectedVideoIndex] && (
                        <Skeleton className="absolute inset-0 rounded-2xl" />
                      )}
                      <video
                        src={game.gameplay[selectedVideoIndex]}
                        autoPlay
                        muted
                        loop
                        playsInline
                        onLoadedData={() => setVideoLoaded(prev => ({ ...prev, [selectedVideoIndex]: true }))}
                        className="w-full h-full rounded-2xl border border-white/10 object-cover"
                      />
                      
                      {/* Metadata overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent rounded-2xl pointer-events-none" />
                      
                      {(() => {
                        // Determine Top Performer using precomputed durations if present
                        let durationsForComparison: number[] = [];
                        if (game.videoDuration && (game.videoDuration as any).length > 0) {
                          durationsForComparison = (game.videoDuration as any).map((d: any) => parseDurationString(d));
                        } else {
                          durationsForComparison = metadataList.filter(Boolean).map((m) => Math.floor(m!.duration));
                        }
                        const longest = durationsForComparison.length > 0 ? Math.max(...durationsForComparison) : 0;
                        const currentDur = game.videoDuration && (game.videoDuration as any).length > selectedVideoIndex
                          ? parseDurationString((game.videoDuration as any)[selectedVideoIndex])
                          : metadataList[selectedVideoIndex]
                          ? Math.floor(metadataList[selectedVideoIndex]!.duration)
                          : 0;
                        const showTop = currentDur === longest && longest > 0;
                        return showTop ? (
                          <div className="absolute top-2 right-2 rounded-full bg-lime-400 px-2 py-1 text-xs font-semibold text-black shadow-lg animate-pulse">
                            Top Performer
                          </div>
                        ) : null;
                      })()}
                      
                      {/* Bottom metadata */}
                      <div className="absolute bottom-2 left-2 right-2 text-white text-xs space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">Video {selectedVideoIndex + 1}</span>
                        </div>
                        <div className="flex items-center gap-2 text-white/80">
                          <span>
                            {(() => {
                              const preDur = game.videoDuration && game.videoDuration.length > selectedVideoIndex ? game.videoDuration[selectedVideoIndex] : undefined;
                              const durVal = preDur ?? (metadataList[selectedVideoIndex] ? metadataList[selectedVideoIndex]!.duration : undefined);
                              return formatDurationDisplay(durVal as number | string | undefined);
                            })()}
                          </span>
                          <span>•</span>
                          <span>
                            {(() => {
                              // Accept new `videoSize`, fallback to legacy `videoLength` and then client metadata
                              const preSize = (game as any).videoSize && (game as any).videoSize.length > selectedVideoIndex
                                ? (game as any).videoSize[selectedVideoIndex]
                                : (game as any).videoLength && (game as any).videoLength.length > selectedVideoIndex
                                ? (game as any).videoLength[selectedVideoIndex]
                                : undefined;
                              const sizeVal = preSize ?? (metadataList[selectedVideoIndex] ? metadataList[selectedVideoIndex]!.size : undefined);
                              return typeof sizeVal === 'number'
                                ? `${(sizeVal / (1024 * 1024)).toFixed(1)}MB`
                                : metadataLoading
                                ? 'Loading...'
                                : 'Unknown';
                            })()}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Download button (only when slot confirmed) */}
                    {confirmed && (
                      <a
                        href={game.gameplay[selectedVideoIndex]}
                        download
                        className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-medium text-white transition hover:bg-white/10 hover:border-white/20"
                      >
                        <Download className="h-3.5 w-3.5" />
                        Download Now
                      </a>
                    )}
                  </div>

                  {/* Video thumbnails/selector */}
                  {game.gameplay.length > 1 && (
                    <div className="flex gap-2">
                      {game.gameplay.slice(0, 3).map((url, idx) => (
                        <button
                          key={url}
                          onClick={() => setSelectedVideoIndex(idx)}
                          className={cn(
                            "flex-1 rounded-xl border transition overflow-hidden",
                            selectedVideoIndex === idx
                              ? "border-lime-400 ring-2 ring-lime-400/20"
                              : "border-white/10 hover:border-white/20"
                          )}
                          style={{ aspectRatio: '9/16' }}
                        >
                          <video
                            src={url}
                            muted
                            playsInline
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div 
                  className="rounded-2xl border border-white/10 bg-white/5 flex items-center justify-center"
                  style={{ aspectRatio: '9/16' }}
                >
                  <span className="text-sm text-white/40 text-center px-4">
                    Unavailable at this time
                  </span>
                </div>
              )}
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
